/** 图片画面配方：所有长度均相对原始图片，不随预览分辨率变化。 */
export type PicturePoint = { x: number; y: number };
export type PicturePathPoint = PicturePoint & {
  in?: PicturePoint;
  out?: PicturePoint;
};
export const PICTURE_BLEND_MODES = [
  "source-over",
  "multiply",
  "screen",
  "overlay",
  "darken",
  "lighten",
  "color-dodge",
  "color-burn",
  "hard-light",
  "soft-light",
  "difference",
  "exclusion",
  "hue",
  "saturation",
  "color",
  "luminosity",
] as const;
export type PictureBlendMode = (typeof PICTURE_BLEND_MODES)[number];
export type PictureMaskShape =
  | "linear"
  | "mirror"
  | "ellipse"
  | "rectangle"
  | "star"
  | "polygon"
  | "brush"
  | "text"
  | "cutout";
export type PictureMaskCombine = "add" | "subtract" | "intersect" | "replace";
export type PictureBrushStroke = {
  points: PicturePoint[];
  size: number;
  erase: boolean;
};
export interface PictureMask {
  id: string;
  name: string;
  shape: PictureMaskShape;
  enabled: boolean;
  combine: PictureMaskCombine;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  feather: number;
  invert: boolean;
  points: PicturePathPoint[];
  strokes: PictureBrushStroke[];
  text: string;
  font: "sans-serif" | "serif" | "monospace";
  maskUrl: string;
}
export interface ImagePicture {
  x?: number;
  y?: number;
  scaleX?: number;
  scaleY?: number;
  rotation?: number;
  opacity?: number;
  blend?: PictureBlendMode;
  radius?: number;
  perspective?: PicturePoint[];
  background?: {
    mode?: "transparent" | "color" | "blur" | "image";
    color?: string;
    blur?: number;
    imageUrl?: string;
  };
  stroke?: {
    enabled?: boolean;
    style?: "solid" | "glow" | "dashed" | "hand";
    color?: string;
    width?: number;
    opacity?: number;
  };
  shadow?: {
    enabled?: boolean;
    color?: string;
    opacity?: number;
    blur?: number;
    distance?: number;
    angle?: number;
  };
  chroma?: {
    enabled?: boolean;
    color?: string;
    tolerance?: number;
    softness?: number;
    spill?: number;
  };
  masks?: PictureMask[];
}
export interface NormalizedImagePicture {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  opacity: number;
  blend: PictureBlendMode;
  radius: number;
  perspective: PicturePoint[];
  background: Required<NonNullable<ImagePicture["background"]>>;
  stroke: Required<NonNullable<ImagePicture["stroke"]>>;
  shadow: Required<NonNullable<ImagePicture["shadow"]>>;
  chroma: Required<NonNullable<ImagePicture["chroma"]>>;
  masks: PictureMask[];
}
const bounded = (
  value: number | undefined,
  initial: number,
  min: number,
  max: number,
) => (Number.isFinite(value) ? Math.max(min, Math.min(max, value!)) : initial);
const color = (value: string | undefined, initial: string) =>
  value && /^#[a-f\d]{6}$/i.test(value) ? value : initial;
export const PICTURE_CORNERS: readonly PicturePoint[] = [
  { x: 0, y: 0 },
  { x: 1, y: 0 },
  { x: 1, y: 1 },
  { x: 0, y: 1 },
];
export function createPictureMask(
  shape: PictureMaskShape,
  id: string,
): PictureMask {
  return {
    id,
    name: "",
    shape,
    enabled: true,
    combine: "add",
    x: 0.5,
    y: 0.5,
    width: 1,
    height: 1,
    rotation: 0,
    feather: 0,
    invert: false,
    points:
      shape === "star"
        ? createPictureStarPoints()
        : [
            { x: 0.1, y: 0.1 },
            { x: 0.9, y: 0.1 },
            { x: 0.8, y: 0.9 },
            { x: 0.2, y: 0.9 },
          ],
    strokes: [],
    text: "",
    font: "sans-serif",
    maskUrl: "",
  };
}
export function normalizeImagePicture(
  input?: ImagePicture | null,
): NormalizedImagePicture {
  return {
    x: bounded(input?.x, 0.5, -2, 3),
    y: bounded(input?.y, 0.5, -2, 3),
    scaleX: bounded(input?.scaleX, 1, 0.01, 5),
    scaleY: bounded(input?.scaleY, 1, 0.01, 5),
    rotation: bounded(input?.rotation, 0, -180, 180),
    opacity: bounded(input?.opacity, 1, 0, 1),
    blend: PICTURE_BLEND_MODES.includes(input?.blend as PictureBlendMode)
      ? input!.blend!
      : "source-over",
    radius: bounded(input?.radius, 0, 0, 0.5),
    perspective: PICTURE_CORNERS.map((point, i) => ({
      x: bounded(input?.perspective?.[i]?.x, point.x, -1, 2),
      y: bounded(input?.perspective?.[i]?.y, point.y, -1, 2),
    })),
    background: {
      mode: ["transparent", "color", "blur", "image"].includes(
        input?.background?.mode ?? "",
      )
        ? input!.background!.mode!
        : "transparent",
      color: color(input?.background?.color, "#ffffff"),
      blur: bounded(input?.background?.blur, 0.04, 0, 0.2),
      imageUrl: input?.background?.imageUrl ?? "",
    },
    stroke: {
      enabled: input?.stroke?.enabled === true,
      style: ["solid", "glow", "dashed", "hand"].includes(
        input?.stroke?.style ?? "",
      )
        ? input!.stroke!.style!
        : "solid",
      color: color(input?.stroke?.color, "#ffffff"),
      width: bounded(input?.stroke?.width, 0.01, 0, 0.2),
      opacity: bounded(input?.stroke?.opacity, 1, 0, 1),
    },
    shadow: {
      enabled: input?.shadow?.enabled === true,
      color: color(input?.shadow?.color, "#000000"),
      opacity: bounded(input?.shadow?.opacity, 0.4, 0, 1),
      blur: bounded(input?.shadow?.blur, 0.03, 0, 0.3),
      distance: bounded(input?.shadow?.distance, 0.03, 0, 0.5),
      angle: bounded(input?.shadow?.angle, 45, -180, 180),
    },
    chroma: {
      enabled: input?.chroma?.enabled === true,
      color: color(input?.chroma?.color, "#00ff00"),
      tolerance: bounded(input?.chroma?.tolerance, 0.15, 0, 1),
      softness: bounded(input?.chroma?.softness, 0.1, 0.001, 1),
      spill: bounded(input?.chroma?.spill, 0.5, 0, 1),
    },
    masks: (input?.masks ?? []).map((mask, index) => {
      const initial = createPictureMask(mask.shape, mask.id ?? `mask-${index}`);
      return {
        ...initial,
        ...mask,
        x: bounded(mask.x, 0.5, -1, 2),
        y: bounded(mask.y, 0.5, -1, 2),
        width: bounded(mask.width, 1, 0.01, 3),
        height: bounded(mask.height, 1, 0.01, 3),
        rotation: bounded(mask.rotation, 0, -180, 180),
        feather: bounded(mask.feather, 0, 0, 0.5),
        points: (mask.points ?? initial.points)
          .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
          .map((p) => ({
            x: bounded(p.x, 0, -1, 2),
            y: bounded(p.y, 0, -1, 2),
            ...(p.in && Number.isFinite(p.in.x) && Number.isFinite(p.in.y)
              ? {
                  in: {
                    x: bounded(p.in.x, 0, -2, 3),
                    y: bounded(p.in.y, 0, -2, 3),
                  },
                }
              : {}),
            ...(p.out && Number.isFinite(p.out.x) && Number.isFinite(p.out.y)
              ? {
                  out: {
                    x: bounded(p.out.x, 0, -2, 3),
                    y: bounded(p.out.y, 0, -2, 3),
                  },
                }
              : {}),
          })),
        strokes: (mask.strokes ?? []).map((s) => ({
          erase: s.erase === true,
          size: bounded(s.size, 0.04, 0.001, 0.5),
          points: s.points
            .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
            .map((p) => ({ x: p.x, y: p.y })),
        })),
        text: (mask.text ?? "").slice(0, 200),
        font: ["sans-serif", "serif", "monospace"].includes(mask.font)
          ? mask.font
          : "sans-serif",
      };
    }),
  };
}
export function hasImagePicture(p: NormalizedImagePicture): boolean {
  return (
    p.x !== 0.5 ||
    p.y !== 0.5 ||
    p.scaleX !== 1 ||
    p.scaleY !== 1 ||
    p.rotation !== 0 ||
    p.opacity !== 1 ||
    p.radius !== 0 ||
    p.blend !== "source-over" ||
    p.background.mode !== "transparent" ||
    p.stroke.enabled ||
    p.shadow.enabled ||
    p.chroma.enabled ||
    p.masks.some((m) => m.enabled) ||
    p.perspective.some(
      (point, i) =>
        point.x !== PICTURE_CORNERS[i].x || point.y !== PICTURE_CORNERS[i].y,
    )
  );
}
export function getImagePictureSignature(p: NormalizedImagePicture): string {
  return JSON.stringify(p);
}

/** 高斯消元计算四点单应矩阵；退化四边形明确失败，不能静默回退为原图。 */
export function pictureHomography(corners: PicturePoint[]): number[] {
  if (corners.length !== 4) throw new Error("invalid-picture-perspective");
  const rows: number[][] = [];
  PICTURE_CORNERS.forEach(({ x: u, y: v }, i) => {
    const { x, y } = corners[i];
    rows.push(
      [u, v, 1, 0, 0, 0, -x * u, -x * v, x],
      [0, 0, 0, u, v, 1, -y * u, -y * v, y],
    );
  });
  for (let c = 0; c < 8; c++) {
    let pivot = c;
    for (let r = c + 1; r < 8; r++)
      if (Math.abs(rows[r][c]) > Math.abs(rows[pivot][c])) pivot = r;
    if (Math.abs(rows[pivot][c]) < 1e-10)
      throw new Error("invalid-picture-perspective");
    [rows[c], rows[pivot]] = [rows[pivot], rows[c]];
    const divisor = rows[c][c];
    for (let k = c; k < 9; k++) rows[c][k] /= divisor;
    for (let r = 0; r < 8; r++)
      if (r !== c) {
        const amount = rows[r][c];
        for (let k = c; k < 9; k++) rows[r][k] -= amount * rows[c][k];
      }
  }
  const result = [...rows.map((row) => row[8]), 1];
  if (
    PICTURE_CORNERS.some((p) => result[6] * p.x + result[7] * p.y + 1 <= 1e-6)
  )
    throw new Error("invalid-picture-perspective");
  return result;
}
export function invertPictureMatrix(m: number[]): number[] {
  const [a, b, c, d, e, f, g, h, i] = m;
  const determinant =
    a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
  if (Math.abs(determinant) < 1e-10)
    throw new Error("invalid-picture-perspective");
  return [
    e * i - f * h,
    c * h - b * i,
    b * f - c * e,
    f * g - d * i,
    a * i - c * g,
    c * d - a * f,
    d * h - e * g,
    b * g - a * h,
    a * e - b * d,
  ].map((v) => v / determinant);
}
export function mapPicturePoint(
  matrix: number[],
  point: PicturePoint,
): PicturePoint {
  const w = matrix[6] * point.x + matrix[7] * point.y + matrix[8];
  if (Math.abs(w) < 1e-10) throw new Error("invalid-picture-perspective");
  return {
    x: (matrix[0] * point.x + matrix[1] * point.y + matrix[2]) / w,
    y: (matrix[3] * point.x + matrix[4] * point.y + matrix[5]) / w,
  };
}

export function pictureMaskToImage(
  mask: PictureMask,
  point: PicturePoint,
  aspect: number,
): PicturePoint {
  const a = (mask.rotation * Math.PI) / 180,
    x = (point.x - 0.5) * mask.width * aspect,
    y = (point.y - 0.5) * mask.height;
  return {
    x: mask.x + (x * Math.cos(a) - y * Math.sin(a)) / aspect,
    y: mask.y + x * Math.sin(a) + y * Math.cos(a),
  };
}
export function imageToPictureMask(
  mask: PictureMask,
  point: PicturePoint,
  aspect: number,
): PicturePoint {
  const a = (-mask.rotation * Math.PI) / 180,
    x = (point.x - mask.x) * aspect,
    y = point.y - mask.y;
  return {
    x: 0.5 + (x * Math.cos(a) - y * Math.sin(a)) / (mask.width * aspect),
    y: 0.5 + (x * Math.sin(a) + y * Math.cos(a)) / mask.height,
  };
}

/** 抠色只改变 alpha 和溢色；无关颜色与已有透明像素保持不变。 */
export function applyPictureChroma(
  data: Uint8ClampedArray,
  chroma: NormalizedImagePicture["chroma"],
): void {
  if (!chroma.enabled) return;
  const key = [1, 3, 5].map(
      (i) => parseInt(chroma.color.slice(i, i + 2), 16) / 255,
    ),
    dominant = key.indexOf(Math.max(...key)),
    kl = 0.299 * key[0] + 0.587 * key[1] + 0.114 * key[2];
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] === 0) continue;
    const red = data[i] / 255, green = data[i + 1] / 255, blue = data[i + 2] / 255;
    // 对亮度变化不敏感的色度距离，兼顾绿幕阴影。
    const l = 0.299 * red + 0.587 * green + 0.114 * blue;
    const distance =
      Math.hypot(red - l - (key[0] - kl), blue - l - (key[2] - kl)) / 1.5;
    const t = Math.max(
      0,
      Math.min(1, (distance - chroma.tolerance) / chroma.softness),
    );
    const alpha = t * t * (3 - 2 * t);
    data[i + 3] = Math.round(data[i + 3] * alpha);
    const neutral = dominant === 0 ? (green + blue) / 2 : dominant === 1 ? (red + blue) / 2 : (red + green) / 2;
    const channel = dominant === 0 ? red : dominant === 1 ? green : blue;
    data[i + dominant] = Math.round(
      (channel -
        Math.max(0, channel - neutral) * chroma.spill * (1 - alpha)) *
        255,
    );
  }
}

/** 二值 alpha 的有向边界；每条边只访问一次，用于描边、虚线、手绘轮廓。 */
export function tracePictureContours(
  alpha: Uint8ClampedArray,
  width: number,
  height: number,
): PicturePoint[][] {
  const stride = width + 1,
    edges = new Map<number, number[]>();
  const opaque = (x: number, y: number) =>
    x >= 0 &&
    y >= 0 &&
    x < width &&
    y < height &&
    alpha[(y * width + x) * 4 + 3] >= 128;
  const add = (a: number, b: number) => {
    const list = edges.get(a);
    if (list) list.push(b);
    else edges.set(a, [b]);
  };
  for (let y = 0; y < height; y++)
    for (let x = 0; x < width; x++)
      if (opaque(x, y)) {
        const tl = y * stride + x,
          tr = tl + 1,
          bl = tl + stride,
          br = bl + 1;
        if (!opaque(x, y - 1)) add(tl, tr);
        if (!opaque(x + 1, y)) add(tr, br);
        if (!opaque(x, y + 1)) add(br, bl);
        if (!opaque(x - 1, y)) add(bl, tl);
      }
  const paths: PicturePoint[][] = [];
  while (edges.size) {
    const start = edges.keys().next().value!;
    let at = start;
    const path: PicturePoint[] = [];
    do {
      path.push({
        x: (at % stride) / width,
        y: Math.floor(at / stride) / height,
      });
      const next = edges.get(at);
      if (!next?.length) break;
      const to = next.pop()!;
      if (!next.length) edges.delete(at);
      at = to;
    } while (at !== start);
    if (path.length >= 3) paths.push(path);
  }
  return paths;
}

export function createPictureStarPoints(
  tips = 5,
  inner = 0.22,
): PicturePathPoint[] {
  return Array.from({ length: tips * 2 }, (_, i) => {
    const a = (i * Math.PI) / tips - Math.PI / 2,
      r = i % 2 ? inner : 0.5;
    return { x: 0.5 + Math.cos(a) * r, y: 0.5 + Math.sin(a) * r };
  });
}
export function smoothPicturePath(
  points: PicturePathPoint[],
): PicturePathPoint[] {
  return points.map((p, i) => {
    const prev = points[(i + points.length - 1) % points.length],
      next = points[(i + 1) % points.length],
      dx = (next.x - prev.x) / 6,
      dy = (next.y - prev.y) / 6;
    return {
      ...p,
      in: { x: p.x - dx, y: p.y - dy },
      out: { x: p.x + dx, y: p.y + dy },
    };
  });
}
export function picturePathData(
  points: PicturePathPoint[],
  width = 1,
  height = 1,
): string {
  if (!points.length) return "";
  let path = `M ${points[0].x * width} ${points[0].y * height}`;
  for (let i = 1; i <= points.length; i++) {
    const a = points[i - 1],
      b = points[i % points.length];
    if (a.out || b.in) {
      const c = a.out ?? a,
        d = b.in ?? b;
      path += ` C ${c.x * width} ${c.y * height} ${d.x * width} ${d.y * height} ${b.x * width} ${b.y * height}`;
    } else path += ` L ${b.x * width} ${b.y * height}`;
  }
  return path + " Z";
}
